/**
 * Jurisdiction-aware consent state and persistence for PostHog product analytics & masked session
 * replay (`MAR-1236` / `MAR-1347` / `MAR-1348`).
 *
 * Precedence:
 * 1. Authenticated `userProfile.analytics_consent` (`true` | `false`) when non-null
 * 2. Browser persisted preference / cookie (`'granted'` | `'denied'`)
 * 3. Jurisdiction regime default (`false` in `'opt-in'` EU/EEA/UK/CH/BR; `true` in `'opt-out'` Americas/SV/US)
 */
import { computed, ref, type Ref } from 'vue'
import {
  ANALYTICS_CONSENT_STORAGE_KEY,
  resolveAnalyticsRegime,
  resolveEffectiveAnalyticsConsent,
  shouldShowConsentBanner,
  type AnalyticsRegime,
  type StoredAnalyticsConsent,
} from '~~/shared/analytics/consent'
import type { UserProfile } from '~~/shared/organizations/userProfile'
import { readPref, writePref } from '~/composables/usePersistedPref'
import { usePostHog } from '~/composables/usePostHog'

function safeUseState<T>(key: string, init: () => T): Ref<T> {
  try {
    if (typeof useState === 'function') {
      return useState<T>(key, init)
    }
  } catch {
    // Fallback for standalone component unit tests outside Nuxt context
  }
  return ref(init()) as Ref<T>
}

function readBrowserStoredConsent(): StoredAnalyticsConsent {
  if (!import.meta.client && typeof window === 'undefined') return null
  const raw = readPref(ANALYTICS_CONSENT_STORAGE_KEY)
  if (raw === 'granted' || raw === 'denied') return raw
  try {
    const match = document.cookie
      .split(';')
      .map(part => part.trim())
      .find(part => part.startsWith(`${ANALYTICS_CONSENT_STORAGE_KEY}=`))
    const val = match?.split('=')[1]
    if (val === 'granted' || val === 'denied') return val
  } catch {
    // Ignore cookie read errors
  }
  return null
}

function writeBrowserStoredConsent(value: 'granted' | 'denied'): void {
  if (!import.meta.client && typeof window === 'undefined') return
  writePref(ANALYTICS_CONSENT_STORAGE_KEY, value)
  try {
    document.cookie = `${ANALYTICS_CONSENT_STORAGE_KEY}=${value}; Path=/; Max-Age=31536000; SameSite=Lax`
  } catch {
    // Ignore cookie write errors
  }
}

function detectClientTimezone(): string | null {
  if (!import.meta.client) return null
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

export function useAnalyticsConsent() {
  let user: Ref<{ uid: string } | null> = ref(null)
  try {
    if (typeof useAuth === 'function') {
      user = useAuth().user as Ref<{ uid: string } | null>
    }
  } catch {
    // Standalone unit test fallback
  }

  let profile: Ref<UserProfile | null> = ref(null)
  let updateProfile: (patch: { analytics_consent?: boolean | null }) => Promise<unknown> = async () => null
  try {
    if (typeof useUserProfile === 'function') {
      const userProfileComposable = useUserProfile()
      profile = userProfileComposable.profile
      updateProfile = userProfileComposable.updateProfile as (patch: { analytics_consent?: boolean | null }) => Promise<unknown>
    }
  } catch {
    // Standalone unit test fallback
  }

  const { syncPostHogWithConsent } = usePostHog()

  const visitorCountry = safeUseState<string | null>('visitor-country', () => null)
  const storedConsent = safeUseState<StoredAnalyticsConsent>('analytics-stored-consent', () => null)
  const storedHydrated = safeUseState<boolean>('analytics-stored-hydrated', () => false)
  const bannerDismissed = safeUseState<boolean>('analytics-banner-dismissed', () => false)
  const preferencesOpen = safeUseState<boolean>('analytics-preferences-open', () => false)
  const savingConsent = safeUseState<boolean>('analytics-consent-saving', () => false)

  if (import.meta.client && !storedHydrated.value) {
    storedConsent.value = readBrowserStoredConsent()
    storedHydrated.value = true
  }

  const regime = computed<AnalyticsRegime>(() =>
    resolveAnalyticsRegime({
      orgJurisdiction: profile.value?.organization_main_jurisdiction ?? null,
      visitorCountry: visitorCountry.value,
      timezone: detectClientTimezone(),
    }),
  )

  const userConsent = computed<boolean | null>(() => {
    const val = profile.value?.analytics_consent
    return typeof val === 'boolean' ? val : null
  })

  const hasExplicitChoice = computed<boolean>(() =>
    typeof userConsent.value === 'boolean' || storedConsent.value === 'granted' || storedConsent.value === 'denied',
  )

  const effectiveConsent = computed<boolean>(() =>
    resolveEffectiveAnalyticsConsent({
      regime: regime.value,
      userConsent: userConsent.value,
      storedConsent: storedConsent.value,
    }),
  )

  const shouldShowBanner = computed<boolean>(() =>
    shouldShowConsentBanner({
      preferencesOpen: preferencesOpen.value,
      bannerDismissed: bannerDismissed.value,
      hasExplicitChoice: hasExplicitChoice.value,
    }),
  )

  function syncAnalyticsEngine(): boolean {
    return syncPostHogWithConsent({
      regime: regime.value,
      userConsent: userConsent.value,
      storedConsent: storedConsent.value,
      identity: user.value?.uid
        ? {
            uid: user.value.uid,
            organizationName: profile.value?.organization_name ?? null,
            organizationMainJurisdiction: profile.value?.organization_main_jurisdiction ?? null,
            orgOverrideId: profile.value?.org_override_id ?? null,
          }
        : null,
    })
  }

  async function setAnalyticsConsent(
    consent: boolean,
    options?: { syncServer?: boolean },
  ): Promise<void> {
    const token: 'granted' | 'denied' = consent ? 'granted' : 'denied'
    storedConsent.value = token
    writeBrowserStoredConsent(token)
    bannerDismissed.value = true
    preferencesOpen.value = false

    if (profile.value) {
      profile.value = {
        ...profile.value,
        analytics_consent: consent,
      }
    }

    syncAnalyticsEngine()

    if (options?.syncServer !== false && user.value?.uid) {
      savingConsent.value = true
      try {
        await updateProfile({ analytics_consent: consent })
        syncAnalyticsEngine()
      } catch {
        // Local storage + cookie preference is already persisted if offline or unverified
      } finally {
        savingConsent.value = false
      }
    }
  }

  function dismissBanner(): void {
    bannerDismissed.value = true
    preferencesOpen.value = false
    // In opt-out jurisdictions, dismissing the notice records the implied 'granted' choice locally
    // so the notice does not reappear on every page reload.
    if (regime.value === 'opt-out' && !hasExplicitChoice.value) {
      storedConsent.value = 'granted'
      writeBrowserStoredConsent('granted')
    }
  }

  function openConsentPreferences(): void {
    preferencesOpen.value = true
  }

  function closeConsentPreferences(): void {
    preferencesOpen.value = false
    bannerDismissed.value = true
  }

  return {
    regime,
    userConsent,
    storedConsent,
    effectiveConsent,
    hasExplicitChoice,
    shouldShowBanner,
    preferencesOpen,
    savingConsent,
    syncAnalyticsEngine,
    setAnalyticsConsent,
    dismissBanner,
    openConsentPreferences,
    closeConsentPreferences,
  }
}
